// src/hooks/useStudents.js
import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useLocation } from '../contexts/LocationContext';

/**
 * Custom hook for real-time student data management
 * Automatically filters by currentLocation from LocationContext
 */
export const useStudents = () => {
  const [allStudentsRaw, setAllStudentsRaw] = useState([]);
  const [attendanceRecordsRaw, setAttendanceRecordsRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLocation } = useLocation();

  useEffect(() => {
    setLoading(true);
    
    const studentsQuery = query(
      collection(db, 'students'),
      orderBy('submissionDate', 'desc')
    );

    const unsubscribeStudents = onSnapshot(
      studentsQuery,
      (snapshot) => {
        const students = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllStudentsRaw(students);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error loading students:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    const attendanceQuery = query(
      collection(db, 'attendance'),
      orderBy('date', 'desc')
    );

    const unsubscribeAttendance = onSnapshot(
      attendanceQuery,
      (snapshot) => {
        const attendance = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAttendanceRecordsRaw(attendance);
      },
      (err) => {
        console.error('Error loading attendance:', err);
      }
    );

    return () => {
      unsubscribeStudents();
      unsubscribeAttendance();
    };
  }, []);

  // ── Location filtering ────────────────────────────────────────────────────
  // 'all' shows everything, otherwise filter by locationId
  // Students without locationId (pre-migration legacy) default to 'redmond'
  const filterByLocation = (records) => {
    if (currentLocation === 'all') return records;
    return records.filter(r => {
      const loc = r.locationId || 'redmond';
      return loc === currentLocation;
    });
  };

  const allStudentsFiltered = filterByLocation(allStudentsRaw);
  const attendanceRecordsFiltered = filterByLocation(attendanceRecordsRaw);

  // ── Format date ───────────────────────────────────────────────────────────
  const formatDate = (date = new Date()) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // ── Merge attendance into students ────────────────────────────────────────
  const studentsWithAttendance = allStudentsFiltered.map(student => {
    const today = formatDate(new Date());
    const todayAttendance = attendanceRecordsFiltered.find(record =>
      record.studentId === student.id && record.date === today
    );
    return {
      ...student,
      attendanceStatus: todayAttendance?.status || null
    };
  });

   // A student counts as active only if not archived AND not marked inactive
  // (e.g. "Discontinued Program"). Used everywhere "active" is checked below.
  const isActive = (s) => !s.archived && s.programStatus !== 'inactive';


  /**
   * Get active (non-archived) students
   */
  const getActiveStudents = () => {
    return studentsWithAttendance.filter(isActive);
  };

  /**
   * Get archived students
   */
  const getArchivedStudents = () => {
    return studentsWithAttendance.filter(s => s.archived);
  };

  /**
   * Get students with pickup service
   */
 const getPickupStudents = () => {
    return studentsWithAttendance.filter(s => isActive(s) && s.needsPickup);
  };

  /**
   * Parse a student's pickupDays field (string or array) into a clean array of day names
   */
  const parsePickupDays = (pickupDays) => {
    if (!pickupDays) return [];
    if (Array.isArray(pickupDays)) return pickupDays.map(d => d.trim());
    return pickupDays.split(/[\s,]+/).filter(Boolean);
  };

  /**
   * Resolve which driver a student has on a given weekday,
   * checking the per-day override first, then falling back to their default driver
   */
  const getDriverForDay = (student, day) => {
    return (student.driverSchedule && student.driverSchedule[day]) || student.assignedDriver;
  };

  /**
   * Get students assigned to a driver on ANY of their pickup days
   * (used for weekly totals / "students on your route" counts)
   */
  const getStudentsByDriver = (driverId) => {
    return studentsWithAttendance.filter(s => {
      if (!isActive(s) || !s.needsPickup) return false;
      const days = parsePickupDays(s.pickupDays);
      if (days.length === 0) return s.assignedDriver === driverId;
      return days.some(day => getDriverForDay(s, day) === driverId);
    });
  };

  /**
   * Get students assigned to a driver on ONE specific day
   * (used for "today's route")
   */
  const getStudentsByDriverForDay = (driverId, day) => {
    return studentsWithAttendance.filter(s => {
      if (!isActive(s) || !s.needsPickup) return false;
      if (getDriverForDay(s, day) !== driverId) return false;
      const days = parsePickupDays(s.pickupDays);
      if (days.length === 0) return true;
      return days.some(d => d.toLowerCase() === day.toLowerCase());
    });
  };

  const getCampStudents = () => {
    return studentsWithAttendance.filter(s => 
      isActive(s) && 
      s.programSelection?.includes('Camp')
    );
  };

  const getStudentsByProgram = (programId) => {
    return studentsWithAttendance.filter(s => 
      isActive(s) && 
      s.programSelection?.toLowerCase().includes(programId.toLowerCase())
    );
  };

  /**
   * Count students by status
   */
  const countByStatus = (status) => {
    return studentsWithAttendance.filter(s => 
      isActive(s) && 
      s.status === status
    ).length;
  };


  /**
   * Get unique schools
   */
  const getUniqueSchools = () => {
    const schools = new Set(
      studentsWithAttendance
        .filter(s => isActive(s) && s.schoolName)
        .map(s => s.schoolName)
    );
    return Array.from(schools);
  };

  return {
    allStudents: studentsWithAttendance,
    attendanceRecords: attendanceRecordsFiltered,
    loading,
    error,
    getActiveStudents,
    getArchivedStudents,
    getPickupStudents,
    getStudentsByDriver,
    getStudentsByDriverForDay,
    getCampStudents,
    getStudentsByProgram,
    countByStatus,
    getUniqueSchools
  };
};

export default useStudents;